/**
 * Level and spectrum readings for the meters (design §7.4).
 *
 * @module
 */

export const FLOOR_DB = -120;

const toDb = (power: number) => (power <= 1e-12 ? FLOOR_DB : Math.max(FLOOR_DB, 10 * Math.log10(power)));

/** Peak and RMS of a block, in dBFS. */
export function levels(samples: Float32Array): { peak: number; rms: number } {
  let peak = 0;
  let sum = 0;
  for (const s of samples) {
    peak = Math.max(peak, Math.abs(s));
    sum += s * s;
  }
  return { peak: toDb(peak * peak), rms: toDb(samples.length ? sum / samples.length : 0) };
}

/**
 * Log-spaced bands from `minHz` to `maxHz` (capped at Nyquist), each the
 * loudest FFT bin in it (dB, from AnalyserNode.getFloatFrequencyData). A band
 * narrower than a bin takes the bin it falls in.
 */
export function bands(spectrum: Float32Array, sampleRate: number, count: number, minHz = 40, maxHz = 20_000): number[] {
  const binHz = sampleRate / 2 / spectrum.length;
  const top = Math.min(maxHz, sampleRate / 2);
  const ratio = top / minHz;
  return Array.from({ length: count }, (_, i) => {
    const lo = minHz * ratio ** (i / count);
    const hi = minHz * ratio ** ((i + 1) / count);
    const first = Math.min(spectrum.length - 1, Math.floor(lo / binHz));
    const last = Math.max(first, Math.min(spectrum.length - 1, Math.ceil(hi / binHz) - 1));
    let max = -Infinity;
    for (let b = first; b <= last; b++) max = Math.max(max, spectrum[b] ?? -Infinity);
    return Number.isFinite(max) ? max : FLOOR_DB;
  });
}

/** Holds each value's recent maximum, then lets it fall at `fallDbPerS` after `holdMs`. */
export class PeakHold {
  private values: number[] = [];
  private since: number[] = [];

  constructor(
    private readonly holdMs = 1000,
    private readonly fallDbPerS = 20,
  ) {}

  update(current: readonly number[], nowMs: number, dtMs: number): readonly number[] {
    this.values = current.map((v, i) => {
      const held = this.values[i] ?? FLOOR_DB;
      if (v >= held) {
        this.since[i] = nowMs;
        return v;
      }
      if (nowMs - (this.since[i] ?? nowMs) < this.holdMs) return held;
      return Math.max(v, held - (this.fallDbPerS * dtMs) / 1000);
    });
    return this.values;
  }
}

/** Inferno-like stops, dark to bright: perceptually ordered, and legible without colour vision. */
const HEAT_STOPS: readonly (readonly [number, number, number])[] = [
  [0, 0, 4],
  [87, 16, 110],
  [188, 55, 84],
  [249, 142, 9],
  [252, 255, 164],
];

/** The colour of `t` (0 to 1, clamped) on the spectrogram's scale. */
export function heat(t: number): [number, number, number] {
  const x = Math.min(1, Math.max(0, t)) * (HEAT_STOPS.length - 1);
  const i = Math.min(HEAT_STOPS.length - 2, Math.floor(x));
  const a = HEAT_STOPS[i] ?? [0, 0, 0];
  const b = HEAT_STOPS[i + 1] ?? a;
  const f = x - i;
  return [0, 1, 2].map((c) => Math.round((a[c] ?? 0) + ((b[c] ?? 0) - (a[c] ?? 0)) * f)) as [number, number, number];
}
